import { SkillLevelValuesEnum, SportsEnum } from 'types/generated/client';
import {
  PLAYING_LEVEL_SPORTS,
  type PlayingLevelValues,
  type RatingSystem,
  SELF_RATE_SKILL_LEVELS,
} from 'utils/shared/playerFinder';
import ChipGroup from 'components/Find/ChipGroup';
import FormTextInput from 'components/Find/FormTextInput';
import SelectableCard from 'components/SelectableCard';
import Switch from 'components/forms/Switch';

interface Props {
  value: PlayingLevelValues;
  onChange: (value: PlayingLevelValues) => void;
}

/**
 * Sports the player plays and how they're rated. Structured systems (VAIR/DUPR/…)
 * are numeric toggles a player can hold several of at once, which is what the
 * user_<sport>_rating_scales tables model. "Self rate" is separate: a single
 * categorical skill level per sport (users.<sport>_skill_level_id), not a scale.
 */
export default function SportsRatingsField({ value, onChange }: Props) {
  const toggleSport = (sport: SportsEnum) => {
    if (!value.sports.includes(sport)) {
      onChange({ ...value, sports: [...value.sports, sport] });
      return;
    }

    // Drop the sport's ratings + self-rate with it — leaving them would silently
    // resurface if the player toggles the sport back on later.
    const ratings = { ...value.ratings };
    delete ratings[sport];
    const selfRatedSkillLevels = { ...value.selfRatedSkillLevels };
    delete selfRatedSkillLevels[sport];

    onChange({
      sports: value.sports.filter((entry) => entry !== sport),
      ratings,
      selfRatedSkillLevels,
    });
  };

  const toggleRatingSystem = (sport: SportsEnum, system: RatingSystem) => {
    const sportRatings = { ...(value.ratings[sport] ?? {}) };
    if (system in sportRatings) {
      delete sportRatings[system];
    } else {
      sportRatings[system] = '';
    }
    onChange({ ...value, ratings: { ...value.ratings, [sport]: sportRatings } });
  };

  const setRating = (sport: SportsEnum, system: RatingSystem, rating: string) => {
    onChange({
      ...value,
      ratings: { ...value.ratings, [sport]: { ...(value.ratings[sport] ?? {}), [system]: rating } },
    });
  };

  const toggleSelfRate = (sport: SportsEnum) => {
    const selfRatedSkillLevels = { ...value.selfRatedSkillLevels };
    if (sport in selfRatedSkillLevels) {
      delete selfRatedSkillLevels[sport];
    } else {
      selfRatedSkillLevels[sport] = null;
    }
    onChange({ ...value, selfRatedSkillLevels });
  };

  const setSelfRatedSkillLevel = (sport: SportsEnum, level: SkillLevelValuesEnum) => {
    onChange({
      ...value,
      selfRatedSkillLevels: { ...value.selfRatedSkillLevels, [sport]: level },
    });
  };

  return (
    <div className="flex flex-col gap-ds-lg">
      {PLAYING_LEVEL_SPORTS.map((sport) => {
        const isSelected = value.sports.includes(sport.value);
        const sportRatings = value.ratings[sport.value] ?? {};
        const isSelfRateOn = sport.value in value.selfRatedSkillLevels;
        const selfRatedLevel = value.selfRatedSkillLevels[sport.value] ?? null;

        return (
          <div key={sport.value} className="flex flex-col gap-ds-md">
            <SelectableCard
              isSelected={isSelected}
              onClick={() => toggleSport(sport.value)}
              className="px-ds-lg py-ds-sm"
            >
              <img src={sport.iconSrc} alt="" className="h-6 w-6 shrink-0" />
              <span className="typography-product-button-label-large">{sport.label}</span>
            </SelectableCard>

            {isSelected && (
              <div className="flex flex-col gap-ds-md rounded-xl border border-color-border-card-lightmode bg-color-bg-lightmode-primary p-ds-md shadow-[inset_0px_4px_8px_0px_rgba(0,0,0,0.08)] dark:border-color-border-card-darkmode dark:bg-color-bg-darkmode-primary">
                <p className="typography-product-subheading-mobile text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  {sport.label} ratings
                </p>

                {sport.ratingSystems.map((system) => {
                  const isOn = system.value in sportRatings;

                  return (
                    <div key={system.value} className="flex flex-col gap-ds-md">
                      <Switch
                        isActive={isOn}
                        toggleIsActive={() => toggleRatingSystem(sport.value, system.value)}
                        labelRight={system.label}
                        labelRightClassName="typography-product-body-highlight text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
                        className="!max-w-none !justify-start gap-ds-md lg:!ml-0"
                      />

                      {isOn && (
                        <FormTextInput
                          value={sportRatings[system.value] ?? ''}
                          onChange={(event) =>
                            setRating(sport.value, system.value, event.target.value)
                          }
                          inputMode="decimal"
                          placeholder="Your rating"
                          aria-label={`${sport.label} ${system.label} rating`}
                          className="text-center"
                        />
                      )}
                    </div>
                  );
                })}

                {/* Self rate — a categorical skill level, not a numeric scale. */}
                <div className="flex flex-col gap-ds-md">
                  <Switch
                    isActive={isSelfRateOn}
                    toggleIsActive={() => toggleSelfRate(sport.value)}
                    labelRight="Self rate"
                    labelRightClassName="typography-product-body-highlight text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
                    className="!max-w-none !justify-start gap-ds-md lg:!ml-0"
                  />

                  {isSelfRateOn && (
                    <ChipGroup
                      options={SELF_RATE_SKILL_LEVELS.map((level) => ({
                        label: level.label,
                        value: level.value as string,
                      }))}
                      value={selfRatedLevel ?? ''}
                      onChange={(level) =>
                        setSelfRatedSkillLevel(sport.value, level as SkillLevelValuesEnum)
                      }
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
